import { z } from 'zod';
import { LegalFormSchema } from '../identity/company.schemas';
import { BuyerContractTemplateKindSchema, FormattedLegalTextSchema } from './buyer-contract-templates.schemas';

export const BROKERAGE_AGREEMENT_PAGE_PATH = '/agreement';

export const BROKERAGE_PAGE_STATES = ['OPEN', 'CONCLUDED', 'WITHDRAWN', 'INACTIVE'] as const;
export const BrokeragePageStateSchema = z.enum(BROKERAGE_PAGE_STATES);
export type BrokeragePageState = z.infer<typeof BrokeragePageStateSchema>;

export const ServeBrokerageAgreementPageBodySchema = z.object({ token: z.string().max(512) });

export const ConcludeBrokerageAgreementBodySchema = z.object({
  token: z.string().max(512),
  pageFingerprint: z.string().max(64),
  // Sent only by a page that shows the read tick (a Muster with `readCheckbox`); ignored otherwise.
  // Not z.literal(true): a false must reach the domain's refusal, not a generic 400.
  noticeRead: z.boolean().optional(),
  earlyStartRequested: z.boolean(),
});

// Not refined to non-empty: the aggregate answers the PRD's refusal with its field list.
export const WithdrawBrokerageAgreementBodySchema = z.object({
  token: z.string().max(512),
  name: z.string().max(200),
  contract: z.string().max(300),
  email: z.string().max(254),
  reason: z.string().max(1000).nullable().optional(),
});

export const BrokeragePageWithdrawalSchema = z.object({
  receivedAt: z.iso.datetime(),
  name: z.string(),
  contract: z.string(),
  email: z.string(),
  reason: z.string().nullable(),
});
export type BrokeragePageWithdrawal = z.infer<typeof BrokeragePageWithdrawalSchema>;

export const BrokeragePageContractSchema = z.object({
  concludedAt: z.iso.datetime(),
  earlyStartRequested: z.boolean(),
  contractNumber: z.string(),
  confirmationSentTo: z.string().nullable(),
  withdrawalPeriodEndsOn: z.iso.date(),
  withdrawalPeriodSentence: z.string(),
  withdrawalAvailable: z.boolean(),
});
export type BrokeragePageContract = z.infer<typeof BrokeragePageContractSchema>;

export const AgencyImprintSchema = z.object({
  companyName: z.string(),
  legalForm: LegalFormSchema.nullable(),
  address: z
    .object({ street: z.string(), houseNumber: z.string(), postalCode: z.string(), city: z.string() })
    .nullable(),
  phone: z.string().nullable(),
  email: z.string().nullable(),
  managingDirectors: z.string().nullable(),
  commercialRegisterCourt: z.string().nullable(),
  commercialRegisterNumber: z.string().nullable(),
  vatId: z.string().nullable(),
  supervisoryAuthority: z.string().nullable(),
  tradePermitAuthority: z.string().nullable(),
  professionalIndemnityInsurance: z.string().nullable(),
});

export const BROKERAGE_EXPOSE_OFFERS = ['DOWNLOAD', 'SOON', 'NONE'] as const;
export const BrokerageExposeOfferSchema = z.enum(BROKERAGE_EXPOSE_OFFERS);
export type BrokerageExposeOffer = z.infer<typeof BrokerageExposeOfferSchema>;

export const BrokeragePageExposeSchema = z.object({ offer: BrokerageExposeOfferSchema });
export type BrokeragePageExpose = z.infer<typeof BrokeragePageExposeSchema>;

export const GetBrokerageExposeDownloadBodySchema = z.object({ token: z.string().max(512) });

// The shipped ReadPropertyExposeResponse shape, deliberately loose on the read side (BL-039).
export const BrokerageExposeDownloadSchema = z.object({ url: z.string(), expiresInSeconds: z.number().int() });
export type BrokerageExposeDownload = z.infer<typeof BrokerageExposeDownloadSchema>;

export const ServedBuyerContractSchema = z.object({
  kind: BuyerContractTemplateKindSchema,
  // null for STANDARD: the apps show its fixed name
  name: z.string().nullable(),
  version: z.string(),
});
export type ServedBuyerContract = z.infer<typeof ServedBuyerContractSchema>;

// Extracted from BrokerageAgreementPageSchema so the snapshot schema can reuse it; the page keeps `.nullable()`.
export const BrokeragePageOfferSchema = z.object({
  pageFingerprint: z.string().length(64),
  musterVersion: z.string(),
  // nullish: snapshots stored before IMM-1431 have no such key, and the Nachweis parses them through this schema.
  template: ServedBuyerContractSchema.nullish(),
  commission: z.string(),
  commissionNote: z.string().nullable(),
  documents: z.object({ contract: z.string(), withdrawalNotice: z.string(), withdrawalForm: z.string() }),
  // Nullish: the standard sends plain strings only, and snapshots stored before the formatting have no such key.
  formattedDocuments: z
    .object({
      contract: FormattedLegalTextSchema,
      withdrawalNotice: FormattedLegalTextSchema,
      withdrawalForm: FormattedLegalTextSchema,
    })
    .nullish(),
  labels: z.object({
    readCheckbox: z.string().nullable(),
    earlyStartCheckbox: z.string(),
    // `.nullish()`: stored v0 snapshots, parsed through this schema on the Nachweis read, have no such keys.
    earlyStartNotice: z.string().nullish(),
    deliveryNotice: z.string().nullish(),
    concludeButton: z.string(),
    facts: z.tuple([z.string(), z.string(), z.string()]),
    privacyNotice: z.string(),
  }),
});

export const BrokerageAgencySchema = z.object({
  companyName: z.string(),
  logoUrl: z.string().nullable(),
  address: z
    .object({ street: z.string(), houseNumber: z.string(), postalCode: z.string(), city: z.string() })
    .nullable(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  privacyPolicyUrl: z.string().nullable(),
  imprint: AgencyImprintSchema,
});
export type BrokerageAgency = z.infer<typeof BrokerageAgencySchema>;

export const BrokerageContactPersonSchema = z.object({
  name: z.string(),
  photoUrl: z.string().nullable(),
  phone: z.string().nullable(),
  email: z.string(),
});
export type BrokerageContactPerson = z.infer<typeof BrokerageContactPersonSchema>;

export const BrokerageAgreementPageSchema = z.object({
  state: BrokeragePageStateSchema,
  agency: BrokerageAgencySchema,
  property: z.object({
    title: z.string(),
    city: z.string().nullable(),
    purchasePrice: z.number().nullable(),
    livingArea: z.number().nullable(),
    rooms: z.number().nullable(),
    coverImageUrl: z.string().nullable(),
  }),
  contactPerson: BrokerageContactPersonSchema,
  buyer: z.object({
    contactType: z.enum(['PERSON', 'COMPANY']),
    salutation: z.enum(['MR', 'MRS', 'MX', 'NONE']).nullable(),
    firstName: z.string().nullable(),
    lastName: z.string().nullable(),
    name: z.string(),
  }),
  offer: BrokeragePageOfferSchema.nullable(),
  contract: BrokeragePageContractSchema.nullable(),
  expose: BrokeragePageExposeSchema.nullable(),
  withdrawal: BrokeragePageWithdrawalSchema.nullable(),
});
export type BrokerageAgreementPage = z.infer<typeof BrokerageAgreementPageSchema>;

/**
 * What the page served, as stored: the OPEN page minus the fingerprint, images as file ids. Stored rows are parsed
 * through this on the Nachweis read, so a change to BrokerageAgreementPageSchema must stay parse-compatible with them.
 */
export const BrokeragePageSnapshotContentSchema = BrokerageAgreementPageSchema.extend({
  agency: BrokerageAgreementPageSchema.shape.agency.omit({ logoUrl: true }).extend({ logoShown: z.boolean() }),
  property: BrokerageAgreementPageSchema.shape.property
    .omit({ coverImageUrl: true })
    .extend({ coverImageFileId: z.string().nullable() }),
  contactPerson: BrokerageAgreementPageSchema.shape.contactPerson
    .omit({ photoUrl: true })
    .extend({ photoFileId: z.string().nullable() }),
  offer: BrokeragePageOfferSchema.omit({ pageFingerprint: true }),
});
export type BrokeragePageSnapshotContent = z.infer<typeof BrokeragePageSnapshotContentSchema>;
